import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import * as i18next from 'i18next';
import { required, minLength, email } from 'vuelidate/lib/validators';
import { G2S } from 'core/g2s';
import * as auth from 'core/auth';
import * as api from 'core/api';
import * as utils from 'core/utils';
// eslint-disable-next-line @typescript-eslint/no-var-requires
const WithRender = require('./template.html');

export interface PanelPromoComponentModel {
    email?: string;
    emailError?: string;
    name?: string;
    nameError?: string;
    phone?: string;
    phoneError?: string;
    submitted: boolean;
}

@WithRender
@Component({
    validations: {
        model: {
            email: { required, email },
            name: { required, minLength: minLength(2) },
            phone: { required, minLength: minLength(5) }
        }
    }
})
export class PanelPromoComponent extends Vue {
    @Prop(Number)
    promotionId: number;

    @Prop()
    promotionUrl: string;

    @Prop()
    promotionVkPost: string;

    @Prop()
    vkGroupEnabled: boolean;

    @Prop()
    registrationEnabled: boolean;

    $auth: auth.AuthService; // Injected in G2S.init

    gaParams: utils.GoogleAnalyticsParams = new utils.GoogleAnalyticsParams();

    model: PanelPromoComponentModel = {
        submitted: false
    };

    phonePlaceholder: string | null = null;

    registerView: string = 'loading';

    get vkPostLabel(): string {
        return this.promotionVkPost ? this.promotionVkPost.replace('https://', '') : '';
    }

    get termsUrl(): string {
        return G2S.config.cdnUrl + '/d/promo/' + this.promotionUrl + '-terms.pdf';
    }

    mounted() {
        this.loadModel();
    }

    loadModel() {
        this.gaParams.dimension11 = this.promotionId.toString();

        (<any>this).$ga.sendPageview('/promo/view', 'Promotion / View', this.gaParams);

        if (this.$auth.authenticated) {
            this.$auth.getCurrentUserAccount().then(
                (result: api.UserAccount) => {
                    if (result.email) {
                        this.model.email = result.email;
                    }
                    if (result.phone) {
                        this.model.phone = result.phone;
                    }
                    if (result.firstName || result.lastName) {
                        this.model.name = result.firstName;

                        if (this.model.name) {
                            this.model.name += ' ';
                        }

                        this.model.name += result.lastName;
                    }

                    if (this.registerView == 'loading') {
                        this.registerView = 'form';
                    }
                },
                () => {
                    (<any>this.$root.$refs).signInPanel.$refs.modalSignIn.show();
                }
            );
        } else {
            if (this.registerView == 'loading') {
                this.registerView = 'form';
            }
        }
    }

    onClose() {
        (<any>this.$refs).modalRegister.isActive = false;
    }

    onPhoneFocus() {
        if (!this.model.phone) {
            this.phonePlaceholder = '+7';
        }
    }

    onPhoneBlur() {
        if (!this.model.phone) {
            this.phonePlaceholder = null;
        }
    }

    onPhoneInput() {
        this.model.phone = utils.String.normalizePhone(
            this.model.phone!
        );
        // Hack to render new value
        (<any>this.$refs).phone.lazyValue = this.model.phone;
    }

    onRegistrationClick() {
        (<any>this.$refs).modalRegister.isActive = true;

        (<any>this).$ga.sendEvent('promotion.view', 'registration.click', 'promotion.view.registration.click', this.gaParams);
    }

    onRegistrationSubmit() {
        (<any>this).$ga.sendEvent('promotion.view', 'registration.submit', 'promotion.view.registration.submit', this.gaParams);

        this.$v.model!.$touch();

        if (!this.$v.model!.$error) {
            this.model.submitted = true;

            const request: api.PromotionMemberCreateRequest = {
                promotionId: this.promotionId,
                email: this.model.email!,
                phone: this.model.phone!,
                name: this.model.name!
            };

            new api.PromotionService().createMember(request).then(
                (result: api.PromotionMemberCreateResult) => {
                    this.model.submitted = false;

                    this.registerView = 'success';

                    if (result.user) {
                        // New user has been created - auto-signin
                        this.$auth.signInClientSide();
                    }

                    (<any>this).$ga.sendPageview('/promo/view/registered', 'Promotion / View / Registered', this.gaParams);

                    utils.PixelsService.setPixelForPromotionRegistration('1');
                },
                (error) => {
                    this.model.submitted = false;

                    (<any>this.$root).$refs.dialogMessage.show({
                        title: i18next.t('error'),
                        text: (error.response.data && error.response.data.localizedMessage) ? error.response.data.localizedMessage : i18next.t('error_server_tryagain')
                    });
                }
            );
        }
    }

    onSocialGroupClick() {
        (<any>this.$refs).modalGroup.isActive = true;

        (<any>this).$ga.sendEvent('promotion.view', 'socialgroup.click', 'promotion.view.socialgroup.click', this.gaParams);
    }

    onSocialPostClick() {
        (<any>this.$refs).modalPost.isActive = true;

        (<any>this).$ga.sendEvent('promotion.view', 'socialpost.click', 'promotion.view.socialpost.click', this.gaParams);
    }
}
